import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import {
  StripeProvider,
  CheckoutForm,
  convertLegacyStripeStyle,
  type LegacyStripeStyle,
} from '@anyroad/stripe';
import { t } from 'services/translation';
import { useCreatePaymentIntent } from './paymentIntentsApi';

interface PaymentProps {
  bookingRequestId: string;
  totalPriceCents: number;
  currency?: string;
  stripeTheme?: LegacyStripeStyle;
}

export interface PaymentHandle {
  submit: () => void;
}

/**
 * Modern Stripe payment integration using refactored @anyroad/stripe components.
 * Demonstrates pattern from scs/checkout/frontend following mobile_first conventions.
 * Integrates with payment intent API endpoints using react-query hooks.
 *
 * @example Usage from parent component:
 * ```tsx
 * const paymentRef = useRef<PaymentHandle>(null);
 *
 * const handleSubmit = () => {
 *   if (paymentRef.current) {
 *     paymentRef.current.submit(); // This triggers the Stripe form submission
 *   }
 * };
 *
 * return (
 *   <>
 *     <Payment ref={paymentRef} {...props} />
 *     <button onClick={handleSubmit}>Submit Payment</button>
 *   </>
 * );
 * ```
 */
export const Payment = forwardRef<PaymentHandle, PaymentProps>(
  ({ bookingRequestId, totalPriceCents, currency, stripeTheme }, ref) => {
    const checkoutFormRef = useRef<HTMLFormElement>(null);

    const appearance = useMemo(() => {
      const converted = stripeTheme
        ? convertLegacyStripeStyle(stripeTheme)
        : undefined;
      return converted;
    }, [stripeTheme]);

    useEffect(() => {
      if (stripeTheme?.fonts && stripeTheme.fonts.length > 0) {
        stripeTheme.fonts.forEach((font) => {
          if (font.cssSrc) {
            const existingLink = document.querySelector(
              `link[href="${font.cssSrc}"]`
            );
            if (!existingLink) {
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = font.cssSrc;
              document.head.appendChild(link);
            }
          }
        });
      }
    }, [stripeTheme]);

    // Create payment intent mutation
    const {
      mutate: createPaymentIntent,
      data: paymentIntentData,
      isPending,
      error,
    } = useCreatePaymentIntent();

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (checkoutFormRef.current) {
            checkoutFormRef.current.requestSubmit();
          }
        },
      }),
      []
    );

    useEffect(() => {
      if (bookingRequestId && totalPriceCents > 0) {
        createPaymentIntent({
          bookingRequestId,
          data: {
            amount_cents: totalPriceCents,
            currency: currency || 'usd',
          },
        });
      }
    }, [bookingRequestId, totalPriceCents, currency]);

    const localizationStrings = {
      errorMessage: t('AN_ERROR_OCCURRED_DURING_PAYMENT_PLEASE_TRY_AGAIN'),
      processingMessage: t('PROCESSING'),
      requiresPaymentMethodMessage: t('PLEASE_ENTER_YOUR_PAYMENT_DETAILS'),
      submitButtonText: t('CONFIRM_PURCHASE'),
      successMessage: t('PAYMENT_SUCCESSFUL'),
    };

    const returnUrl = `/booking_request/confirmation?id=${bookingRequestId}`;

    if (isPending) {
      return <div>{t('PROCESSING')}</div>;
    }

    if (error || !paymentIntentData || !paymentIntentData.client_secret) {
      return (
        <div>
          {error?.message ||
            t('AN_ERROR_OCCURRED_DURING_PAYMENT_PLEASE_TRY_AGAIN')}
        </div>
      );
    }

    return (
      <StripeProvider
        appearance={appearance}
        clientSecret={paymentIntentData.client_secret}
        connectedAccountId={paymentIntentData.managed_account_token || null}
        publishableKey={process.env.STRIPE_PUBLISHABLE_KEY || ''}
      >
        <CheckoutForm
          ref={checkoutFormRef}
          dataTestId="mobile-first-payment"
          hideButton
          localizationStrings={localizationStrings}
          returnUrl={returnUrl}
        />
      </StripeProvider>
    );
  }
);

Payment.displayName = 'Payment';
